export type MeetingStatus =
  | 'pending' | 'bot_joining' | 'recording'
  | 'transcribing' | 'summarizing' | 'complete'
  | 'bot_failed' | 'transcript_failed' | 'summary_failed'
  | 'bot_removed';

export type Platform = 'zoom' | 'google_meet' | 'teams' | 'unknown';

export interface KeywordHit {
  keywordId: string;
  phrase: string;
  hitCount: number;
}

export interface MeetingListItem {
  id: string;
  title: string | null;
  platform: Platform | null;
  status: MeetingStatus;
  templateType: string | null;
  startedAt: number | null;
  endedAt: number | null;
  durationSeconds: number | null;
  createdAt: number;
  hasVideo: boolean;
  videoStored?: boolean;
  hasSummary?: boolean;
  errorMessage: string | null;
  keywordHits?: KeywordHit[];
  searchSnippet?: string | null;
}

export interface TranscriptLine {
  speakerLabel: string;
  speakerName: string | null;
  text: string;
  startMs: number;
  endMs: number;
}

export interface KeyTopic { topic: string; timestampMs?: number }
export interface ActionItem { text: string; owner?: string | null }

export type DecisionStatus = 'proposed' | 'accepted' | 'rejected' | 'deferred';
export type ActionStatus = 'open' | 'done' | 'cancelled';
export type ActionPriority = 'low' | 'normal' | 'high';
export type LedgerSource = 'extract' | 'manual';

export interface Decision {
  id: string;
  meetingId: string;
  text: string;
  decidedBy: string | null;
  status: DecisionStatus;
  evidenceStartMs: number | null;
  evidenceEndMs: number | null;
  source: LedgerSource;
  createdAt: number;
  updatedAt: number;
}

export interface ExternalRef {
  provider: string;
  id: string;
  url: string | null;
}

export interface ActionPushState {
  id: string;
  destination: string;
  status: string;
  attempts: number;
  lastError: string | null;
  externalId: string | null;
  externalUrl: string | null;
  updatedAt: number;
}

export interface MeetingAction {
  id: string;
  meetingId: string;
  text: string;
  owner: string | null;
  dueDate: string | null;
  status: ActionStatus;
  priority: ActionPriority | null;
  evidenceStartMs: number | null;
  source: LedgerSource;
  externalRefs: ExternalRef[];
  lastPush: ActionPushState | null;
  createdAt: number;
  updatedAt: number;
}

export interface PushDefaults {
  basecamp: { projectId: string | null; todolistId: string | null; autoPush: boolean };
  emailTo: string | null;
}

export interface MeetingPushDefaults {
  meeting: PushDefaults;
  account: PushDefaults;
  effective: PushDefaults;
}

export interface ApiKeyRecord {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number | null;
  token?: string;
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  secretPrefix: string;
  events: string[];
  enabled: boolean;
  createdAt: number;
  secret?: string;
}

export interface WebhookDelivery {
  id: string;
  eventId: string;
  eventType: string;
  meetingId: string | null;
  status: string;
  attempts: number;
  lastError: string | null;
  responseStatus: number | null;
  nextAttemptAt: number | null;
  deliveredAt: number | null;
  createdAt: number;
}

export interface SeatWorkItem {
  id: string;
  status: string;
  summary: string;
  links: Array<{ label: string; url: string }>;
  notes: string | null;
  finishedAt: number | null;
}

export interface Summary {
  tldr: string;
  keyTopics: KeyTopic[];
  actionItems: ActionItem[];
  nextSteps: string[];
  markdown: string;
  templateType: string;
}

export interface MeetingDetail {
  id: string;
  title: string | null;
  meetingUrl: string;
  platform: Platform | null;
  status: MeetingStatus;
  templateType: string | null;
  startedAt: number | null;
  endedAt: number | null;
  durationSeconds: number | null;
  videoUrl: string | null;
  /** Recall media URL exists (source for the cloud copy). */
  hasRecallVideo?: boolean;
  /** Durable copy exists in Cloudflare R2. */
  videoStored?: boolean;
  videoStoredAt?: number | null;
  /** Copy to R2 queued / in progress. */
  videoStoring?: boolean;
  /** Durable MP3 exists in Cloudflare R2. */
  audioStored?: boolean;
  /** ffmpeg extract (or Recall MP3 copy) is in progress. */
  audioPreparing?: boolean;
  cloudStorageConfigured?: boolean;
  /** Public share link, e.g. https://takenotes.us/v/<token> */
  shareUrl?: string | null;
  errorMessage: string | null;
  transcript: TranscriptLine[];
  summary: Summary | null;
  seatWork?: SeatWorkItem[];
  keywordHits?: KeywordHit[];
  basecampConnected?: boolean;
  agentSeat?: AgentSeatState | null;
}

export interface AgentSeatTurn {
  id: string;
  heard: string | null;
  said: string | null;
  suggestion: string | null;
  createdAt: number;
}

export interface AgentSeatState {
  displayName: string;
  pronouns: 'she/her';
  persona: string;
  status: string;
  muted: boolean;
  stopped: boolean;
  sameBot: boolean;
  turns: AgentSeatTurn[];
}

export interface JoinInput {
  meetingUrl: string;
  templateType?: string;
  title?: string;
  /** Explicit opt-in. Absent means a silent notetaker. */
  agentSeat?: { persona: 'basecamp'; speak: true; video: true };
}

export interface JoinResult {
  meetingId: string;
  botId: string;
  status: string;
}

export interface AskResult {
  id: string;
  question: string;
  answer: string;
  source: string;
  createdAt: number;
}

export interface Highlight {
  id: string;
  startMs: number;
  endMs: number;
  label: string | null;
  source: string;
  createdAt: number;
  clip?: { videoUrl: string; startMs: number; endMs: number } | null;
}

export interface CalendarAccount {
  id: string;
  provider: string;
  email: string | null;
  name: string | null;
  autoJoin: boolean;
  connectedAt: number;
}

export interface CalendarEvent {
  id: string;
  provider: string;
  title: string | null;
  startAt: number;
  endAt: number | null;
  meetingUrl: string | null;
  htmlLink: string | null;
  meetingId: string | null;
  joinStatus: string;
  accountId: string;
}

export interface CalendarAccountsResponse {
  accounts: CalendarAccount[];
  basecamp: { email: string | null; name: string | null; connectedAt: number } | null;
  encryptionReady: boolean;
  googleConfigured: boolean;
  microsoftConfigured: boolean;
  basecampConfigured: boolean;
}


export interface ClaudeModelInfo {
  id: string;
  displayName?: string;
  createdAt?: number;
}

export interface ModelsSettings {
  primary: string;
  backup: string;
  models: ClaudeModelInfo[];
  refreshedAt: number | null;
  fetchError?: string;
}

const LEGACY_TOKEN_KEY = 'takenotes.apiToken';
export const AUTH_REQUIRED_EVENT = 'takenotes:auth-required';

/** The browser no longer uses the global API bearer; clear any stored copy. */
export function clearLegacyApiToken(): void {
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
  }
}

async function parseError(res: Response): Promise<ApiError> {
  let message = `${res.status} ${res.statusText}`;
  let code: string | undefined;
  try {
    const body = await res.json();
    if (body && typeof body === 'object' && 'error' in body) {
      code = String((body as { error: string }).error);
      message = 'message' in body ? String((body as { message: string }).message) : code;
    }
  } catch {
    /* body was not JSON */
  }
  return new ApiError(message, res.status, code);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
    throw new ApiError('Your session has ended. Please sign in again.', 401, 'unauthorized');
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Sign-in endpoints: a 401 means bad credentials, not an expired session. */
async function authPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

export type UserRole = 'admin' | 'member';
export type UserStatus = 'active' | 'invited' | 'disabled';

export interface UserRow {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  status: UserStatus;
  hasPassword: boolean;
  loginTokens: number;
  lastLoginAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface LoginToken {
  id: string;
  userId: string;
  label: string;
  prefix: string;
  createdAt: number;
  lastUsedAt: number | null;
}

export interface Me {
  authenticated: boolean;
  email?: string;
  name?: string | null;
  role?: UserRole;
  userId?: string;
  hasPassword?: boolean;
  ssoConfigured: boolean;
  passwordAuth?: boolean;
  /** True when the server can send password reset emails. */
  passwordResetEmail?: boolean;
  openAccess?: boolean;
}

export interface SignInResult {
  ok: true;
  email: string;
  name: string | null;
  role: UserRole;
  userId: string;
}

export const api = {
  listMeetings: (q?: string) =>
    request<MeetingListItem[]>(q ? `/api/meetings?q=${encodeURIComponent(q)}` : '/api/meetings'),
  getMeeting: (id: string) => request<MeetingDetail>(`/api/meetings/${id}`),
  join: (input: JoinInput) =>
    request<JoinResult>('/api/meetings/join', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  renameSpeaker: (id: string, speakerLabel: string, speakerName: string) =>
    request<{ ok: true }>(`/api/meetings/${id}/speakers/rename`, {
      method: 'POST',
      body: JSON.stringify({ speakerLabel, speakerName }),
    }),
  resummarize: (id: string, templateType?: string) =>
    request<{ ok: true; status: string }>(`/api/meetings/${id}/resummarize`, {
      method: 'POST',
      body: JSON.stringify(templateType ? { templateType } : {}),
    }),
  ask: (id: string, question: string) =>
    request<AskResult>(`/api/meetings/${id}/ask`, {
      method: 'POST',
      body: JSON.stringify({ question }),
    }),
  listAsks: (id: string) => request<AskResult[]>(`/api/meetings/${id}/asks`),
  listHighlights: (id: string) => request<Highlight[]>(`/api/meetings/${id}/highlights`),
  createHighlight: (id: string, startMs: number, endMs: number, label?: string) =>
    request<Highlight>(`/api/meetings/${id}/highlights`, {
      method: 'POST',
      body: JSON.stringify({ startMs, endMs, label }),
    }),
  deleteHighlight: (id: string, hid: string) =>
    request<{ ok: true }>(`/api/meetings/${id}/highlights/${hid}`, { method: 'DELETE' }),
  postBasecamp: (id: string, projectId: string, createTodos?: boolean) =>
    request<{ ok: true; messageUrl?: string; todosCreated: number }>(`/api/meetings/${id}/basecamp`, {
      method: 'POST',
      body: JSON.stringify({ projectId, createTodos }),
    }),
  calendarAccounts: () => request<CalendarAccountsResponse>('/api/calendar/accounts'),
  calendarEvents: () => request<CalendarEvent[]>('/api/calendar/events'),
  setAutoJoin: (accountId: string, autoJoin: boolean) =>
    request<{ ok: true }>(`/api/calendar/accounts/${accountId}`, {
      method: 'PATCH',
      body: JSON.stringify({ autoJoin }),
    }),
  disconnectAccount: (accountId: string) =>
    request<{ ok: true }>(`/api/calendar/accounts/${accountId}`, { method: 'DELETE' }),
  syncCalendar: () => request<{ ok: true }>('/api/calendar/sync', { method: 'POST' }),
  listKeywords: () => request<Array<{ id: string; phrase: string; createdAt: number }>>('/api/keywords'),
  createKeyword: (phrase: string) =>
    request<{ id: string; phrase: string }>(`/api/keywords`, {
      method: 'POST',
      body: JSON.stringify({ phrase }),
    }),
  deleteKeyword: (id: string) => request<{ ok: true }>(`/api/keywords/${id}`, { method: 'DELETE' }),
  getModelsSettings: () => request<ModelsSettings>('/api/settings/models'),
  refreshModels: () => request<ModelsSettings>('/api/settings/models?refresh=1'),
  saveModelsSettings: (primary: string, backup: string) =>
    request<ModelsSettings>('/api/settings/models', {
      method: 'PUT',
      body: JSON.stringify({ primary, backup }),
    }),
  me: () => request<Me>('/api/auth/me'),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  audioStatus: (id: string, opts?: { retry?: boolean }) =>
    request<{ status: 'ready' | 'preparing' | 'failed' | 'idle'; message?: string }>(
      `/api/meetings/${id}/audio?status=1${opts?.retry ? '&retry=1' : ''}`,
    ),
  videoStatus: (id: string) =>
    request<{ status: 'ready' | 'unavailable'; message?: string }>(
      `/api/meetings/${id}/video?status=1`,
    ),
  storeVideo: (id: string) =>
    request<{ ok: true; status?: string; alreadyStored?: boolean; shareUrl?: string | null }>(
      `/api/meetings/${id}/store-video`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  deleteMeeting: (id: string) =>
    request<{ ok: true }>(`/api/meetings/${id}`, { method: 'DELETE' }),
  /** Kick the notetaker bot out of a live meeting. */
  leaveMeeting: (id: string) =>
    request<{ ok: true; status: string; alreadyLeft: boolean; awaitingMedia: boolean }>(
      `/api/meetings/${id}/leave`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  inviteBasecamp: (id: string) =>
    request<{ ok: true; botId: string; displayName: string; pronouns: 'she/her' }>(
      `/api/meetings/${id}/agent-seat`,
      { method: 'POST', body: JSON.stringify({ persona: 'basecamp', speak: true, video: true }) },
    ),
  muteBasecamp: (id: string, muted: boolean) =>
    request<{ ok: true; muted: boolean }>(`/api/meetings/${id}/agent-seat/mute`, {
      method: 'POST',
      body: JSON.stringify({ muted }),
    }),
  kickBasecamp: (id: string) =>
    request<{ ok: true; stopped: boolean; meetingStatus: string; alreadyLeft: boolean }>(
      `/api/meetings/${id}/agent-seat/kick`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  bulkDeleteMeetings: (ids: string[]) =>
    request<{ ok: true; deleted: number }>('/api/meetings/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  login: (email: string, password: string) =>
    authPost<SignInResult>('/api/auth/login', { email, password }),
  tokenLogin: (token: string) => authPost<SignInResult>('/api/auth/token-login', { token }),
  checkInvite: (token: string) =>
    authPost<{ email: string; name: string | null; hasPassword: boolean }>('/api/auth/invite/check', { token }),
  register: (inviteToken: string, password: string, name?: string) =>
    authPost<SignInResult>('/api/auth/register', { inviteToken, password, name }),
  requestPasswordReset: (email: string) =>
    authPost<{ ok: true; emailEnabled: boolean }>('/api/auth/password-reset/request', { email }),
  checkPasswordReset: (token: string) =>
    authPost<{ email: string }>('/api/auth/password-reset/check', { token }),
  completePasswordReset: (token: string, password: string) =>
    authPost<SignInResult>('/api/auth/password-reset/complete', { token, password }),
  myTokens: () => request<LoginToken[]>('/api/account/tokens'),
  createMyToken: (label?: string, token?: string) =>
    request<LoginToken & { token: string }>('/api/account/tokens', {
      method: 'POST',
      body: JSON.stringify({ label: label?.trim() || undefined, token: token || undefined }),
    }),
  revokeMyToken: (id: string) =>
    request<{ ok: true }>(`/api/account/tokens/${id}`, { method: 'DELETE' }),
  changePassword: (newPassword: string, currentPassword?: string) =>
    request<{ ok: true }>('/api/account/password', {
      method: 'POST',
      body: JSON.stringify({ newPassword, currentPassword }),
    }),
  listUsers: () => request<UserRow[]>('/api/users'),
  inviteUser: (input: { email: string; role?: UserRole; name?: string; password?: string }) =>
    request<UserRow & { setupUrl: string | null }>('/api/users/invite', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateUser: (id: string, patch: { role?: UserRole; status?: UserStatus; name?: string | null }) =>
    request<UserRow>(`/api/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  setUserPassword: (id: string, password: string) =>
    request<UserRow>(`/api/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  userSetupLink: (id: string) =>
    request<{ setupUrl: string; expiresInDays: number }>(`/api/users/${id}/setup-link`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  userTokens: (id: string) => request<LoginToken[]>(`/api/users/${id}/tokens`),
  createUserToken: (id: string, label?: string, token?: string) =>
    request<LoginToken & { token: string }>(`/api/users/${id}/tokens`, {
      method: 'POST',
      body: JSON.stringify({ label: label?.trim() || undefined, token: token || undefined }),
    }),
  revokeUserToken: (id: string, tid: string) =>
    request<{ ok: true }>(`/api/users/${id}/tokens/${tid}`, { method: 'DELETE' }),
  listDecisions: (meetingId: string) => request<Decision[]>(`/api/v1/meetings/${meetingId}/decisions`),
  listActions: (meetingId: string) => request<MeetingAction[]>(`/api/v1/meetings/${meetingId}/actions`),
  createDecision: (meetingId: string, text: string, decidedBy?: string) =>
    request<Decision>(`/api/v1/meetings/${meetingId}/decisions`, {
      method: 'POST',
      body: JSON.stringify({ text, decidedBy: decidedBy || null, status: 'proposed' }),
    }),
  createAction: (meetingId: string, text: string, owner?: string) =>
    request<MeetingAction>(`/api/v1/meetings/${meetingId}/actions`, {
      method: 'POST',
      body: JSON.stringify({ text, owner: owner || null, status: 'open' }),
    }),
  updateDecision: (id: string, patch: Partial<Pick<Decision, 'text' | 'decidedBy' | 'status'>>) =>
    request<Decision>(`/api/v1/decisions/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  updateAction: (id: string, patch: Partial<Pick<MeetingAction, 'text' | 'owner' | 'status'>>) =>
    request<MeetingAction>(`/api/v1/actions/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  pushAction: (id: string, body: { destination: 'basecamp' | 'email'; projectId?: string; todolistId?: string; to?: string }) =>
    request<{ ok: true; idempotent: boolean; action: MeetingAction; externalRef: ExternalRef | null }>(
      `/api/v1/actions/${id}/push`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  pushAllActions: (meetingId: string, body: { destination: 'basecamp' | 'email'; emailTo?: string; projectId?: string }) =>
    request<{ results: Array<{ actionId: string; ok: boolean; error: string | null; message: string | null }> }>(
      `/api/v1/meetings/${meetingId}/actions/push-all`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  getPushDefaults: () => request<PushDefaults>('/api/settings/push-defaults'),
  savePushDefaults: (body: { projectId?: string | null; todolistId?: string | null; autoPush?: boolean; emailTo?: string | null }) =>
    request<PushDefaults>('/api/settings/push-defaults', { method: 'PUT', body: JSON.stringify(body) }),
  getMeetingPushDefaults: (meetingId: string) =>
    request<MeetingPushDefaults>(`/api/v1/meetings/${meetingId}/push-defaults`),
  saveMeetingPushDefaults: (meetingId: string, body: { projectId?: string | null; todolistId?: string | null; emailTo?: string | null }) =>
    request<{ meeting: PushDefaults; effective: PushDefaults }>(`/api/v1/meetings/${meetingId}/push-defaults`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  listApiKeys: () => request<ApiKeyRecord[]>('/api/settings/developers/api-keys'),
  createApiKey: (name: string, scopes: string[]) =>
    request<ApiKeyRecord>('/api/settings/developers/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name, scopes }),
    }),
  revokeApiKey: (id: string) =>
    request<{ ok: true }>(`/api/settings/developers/api-keys/${id}`, { method: 'DELETE' }),
  listWebhooks: () => request<WebhookEndpoint[]>('/api/settings/developers/webhooks'),
  createWebhook: (url: string, events: string[]) =>
    request<WebhookEndpoint>('/api/settings/developers/webhooks', {
      method: 'POST',
      body: JSON.stringify({ url, events }),
    }),
  deleteWebhook: (id: string) =>
    request<{ ok: true }>(`/api/settings/developers/webhooks/${id}`, { method: 'DELETE' }),
  listWebhookDeliveries: (id: string) =>
    request<WebhookDelivery[]>(`/api/settings/developers/webhooks/${id}/deliveries`),
};

export const TEMPLATES: Array<{ value: string; label: string }> = [
  { value: 'general', label: 'General Meeting' },
  { value: 'sales_call', label: 'Sales Call' },
  { value: 'team_standup', label: 'Team Standup' },
  { value: 'client_check_in', label: 'Client Check-in' },
  { value: 'brainstorm', label: 'Brainstorm' },
  { value: 'interview', label: 'Interview' },
  { value: 'one_on_one', label: '1:1' },
  { value: 'retrospective', label: 'Retrospective' },
  { value: 'product_review', label: 'Product Review' },
  { value: 'customer_discovery', label: 'Customer Discovery' },
  { value: 'weekly_review', label: 'Weekly Review' },
  { value: 'kickoff', label: 'Project Kickoff' },
  { value: 'status_update', label: 'Status Update' },
  { value: 'training', label: 'Training' },
];

export function statusLabel(status: MeetingStatus): string {
  switch (status) {
    case 'pending': return 'Pending';
    case 'bot_joining': return 'Joining';
    case 'recording': return 'Recording';
    case 'transcribing': return 'Transcribing';
    case 'summarizing': return 'Summarizing';
    case 'complete': return 'Complete';
    case 'bot_failed': return 'Bot failed';
    case 'transcript_failed': return 'Transcript failed';
    case 'summary_failed': return 'Summary failed';
    case 'bot_removed': return 'Notetaker removed';
  }
}

export function statusKind(status: MeetingStatus): 'active' | 'complete' | 'failed' | 'idle' {
  if (status === 'complete') return 'complete';
  if (status.endsWith('_failed')) return 'failed';
  if (status === 'pending' || status === 'bot_removed') return 'idle';
  return 'active';
}

/** Statuses where the Recall bot may still be in (or joining) the call. */
export function isLiveBotStatus(status: MeetingStatus): boolean {
  return status === 'pending' || status === 'bot_joining' || status === 'recording';
}

export function isTerminal(status: MeetingStatus): boolean {
  return status === 'complete' || status === 'bot_removed' || status.endsWith('_failed');
}

export function templateLabel(value: string | null | undefined): string {
  if (!value) return 'General Meeting';
  return TEMPLATES.find((t) => t.value === value)?.label ?? value;
}
